import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { LaborTime } from '@1po/1po-bff-fe-spec/generated/catalog/easy_basket/response/GetEasyBasketOperationDetailResponse';
import { Popover } from 'antd';
import { ROUTER_SETTINGS_ESTIMATE } from 'app/AppRouter';
import { RootState } from 'app/AppStore';
import { CheckIcon, CogIcon, PlusOutlinedIcon } from 'assets/icons';
import { DataContainer } from 'components/DataContainer';
import { InfoDialog, useDisclosure } from 'components/Dialog';
import {
  addLaborTimeSelection,
  getSelectedLaborTime,
  removeLaborTimeSelection,
} from 'domains/easyBasket/EasyBasket.store';
import { FocusSetting } from 'domains/estimate/Estimate.types';
import { getCurrency } from 'domains/user';
import { IconWrapper } from 'pages/CatalogPage/DH/SubcategorySection/LaborTimeSection/LaborTimeSection.styled';
import {
  ConnectionLink,
  Crosslink,
  CrosslinkCardWrapper,
  EasyBasketCardWrapper,
  NarrowLink,
  SelectedQuantity,
} from 'pages/CatalogPage/DH/SubcategorySection/OperationsSection/PartsSelection/PartsSelection.styled';
import {
  MIDDLE,
  PartPosition,
  useCalculateLaborTimePrices,
  useIsAnyGoSelected,
} from 'pages/CatalogPage/DH/SubcategorySection/OperationsSection/utils';
import { SETTINGS_RATES } from 'pages/SettingsPage/Estimates/EstimateSettings';
import { theme } from 'styles';
import { BlackButton, Box, Flex, Icon, Text, WhiteButton } from 'UI';
import { getData, textFormatter } from 'utils';

interface LaborTimeProps {
  itemGroup: string;
  isMultipleLaborTimesGroup: boolean;
  laborTime: LaborTime;
  mainLaborTimePosition: PartPosition;
  isMainGOLaborTime: boolean;
  isOnlySOLaborTime: boolean;
}

const LaborTimeCard = ({
  itemGroup,
  isMultipleLaborTimesGroup,
  laborTime,
  mainLaborTimePosition,
  isMainGOLaborTime,
  isOnlySOLaborTime,
}: LaborTimeProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const disclosure = useDisclosure();
  const { onOpen, onClose } = disclosure;
  const history = useHistory();
  const currency = useSelector(getCurrency);
  const prices = useCalculateLaborTimePrices(laborTime.businessSpecificities, laborTime.technicality);
  const pricesData = getData(prices);
  const selectedLaborTimes = useSelector((state: RootState) => getSelectedLaborTime(state, itemGroup));
  const isAnyGoSelected = useIsAnyGoSelected();
  const isSelected = selectedLaborTimes?.laborTimeId === laborTime.id;
  const canSelect = !!pricesData?.vatInclPrice && !!pricesData?.vatExclPrice;

  const getLaborTimeType = () => {
    switch (laborTime.operationType) {
      case '1':
        return 'GO';
      case '2':
        return 'SO';
      default:
        return '';
    }
  };

  const selectLaborTime = () => {
    if (!canSelect) {
      return;
    }
    dispatch(
      addLaborTimeSelection({
        itemGroup,
        laborTime: {
          laborTimeId: laborTime.id,
          operationType: laborTime.operationType,
          price: {
            priceVatIncluded: pricesData?.vatInclPrice ?? '0',
            priceVatExcluded: pricesData?.vatExclPrice ?? '0',
            currency,
          },
        },
      }),
    );
  };

  const handleSelectLaborTime = () => {
    if (laborTime.operationType === '1' && isAnyGoSelected && selectedLaborTimes?.operationType !== '1') {
      onOpen();
    } else {
      selectLaborTime();
    }
  };

  useEffect(() => {
    if (isMainGOLaborTime || isOnlySOLaborTime) {
      selectLaborTime();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderLaborTimeCard = () => (
    <EasyBasketCardWrapper
      padding={'0 15px 0 15px'}
      align={'center'}
      minHeight={92}
      gap={10}
      justify={'space-between'}
      isAlternative
      multipleReferencesGroup={isMultipleLaborTimesGroup}
    >
      <Flex>
        <Text type="base" bold>
          {laborTime.id}
        </Text>
      </Flex>
      <Flex justify={'center'}>{getLaborTimeType()}</Flex>
      <Flex justify={'center'}>{laborTime?.duration ?? ''}</Flex>
      <Flex justify={'center'}>{laborTime?.characteristics?.quantity ?? ''}</Flex>
      <Flex justify={'center'} minWidth={250}>
        <Flex direction={'column'}>{laborTime?.characteristics?.elementPrecision ?? ''}</Flex>
      </Flex>
      <Flex justify={'center'}>
        <DataContainer
          data={prices}
          NotFound={() => (
            <Popover
              content={`${t(
                'catalog.parts.category.car_parts.labor_time.notification.set_technicity',
                'Set the hourly rate of technicity',
              )}`}
              placement={'bottomLeft'}
              trigger="hover"
            >
              <IconWrapper>
                <Icon
                  IconComponent={CogIcon}
                  color={theme.color.clear_blue}
                  height={22}
                  width={23}
                  onClick={() => {
                    const params = new URLSearchParams();
                    params.set(FocusSetting, SETTINGS_RATES);
                    history.push(`${ROUTER_SETTINGS_ESTIMATE}?${params}`);
                  }}
                />
              </IconWrapper>
            </Popover>
          )}
        >
          <Flex direction={'column'} align={'center'}>
            <Text type={'h5_bold'} noWrap>
              {t('order.client_price_VAT_included', '{{ price }} VAT. Incl', {
                price: textFormatter.formatCurrency(pricesData?.vatInclPrice ?? '', currency),
              })}
            </Text>
            <Text type={'h5_light'} noWrap>
              {t('order.client_price_VAT_excluded', '{{ price }} VAT. Excl', {
                price: textFormatter.formatCurrency(pricesData?.vatExclPrice ?? '', currency),
              })}
            </Text>
          </Flex>
        </DataContainer>
      </Flex>
      <Flex justify={'flex-end'} maxWidth={100}>
        {isSelected ? (
          <SelectedQuantity onClick={() => dispatch(removeLaborTimeSelection({ itemGroup }))}>
            <Icon IconComponent={CheckIcon} color={theme.color.white} size={20} />
          </SelectedQuantity>
        ) : (
          <Icon IconComponent={PlusOutlinedIcon} onClick={handleSelectLaborTime} disabled={!canSelect} />
        )}
      </Flex>
    </EasyBasketCardWrapper>
  );

  const renderMainReferenceCrosslink = (position?: PartPosition) => {
    if (isMultipleLaborTimesGroup) {
      if (position === MIDDLE) {
        return (
          <Box width={30}>
            <NarrowLink />
            <ConnectionLink />
          </Box>
        );
      }
      return (
        <Box width={30}>
          <Crosslink position={position} />
        </Box>
      );
    }
    return <Box width={30} />;
  };

  return (
    <CrosslinkCardWrapper>
      {renderMainReferenceCrosslink(mainLaborTimePosition)}
      {renderLaborTimeCard()}
      <InfoDialog
        disclosure={disclosure}
        title={t(
          'catalog.easy_basket.total_strip.dialog_general_operations.title',
          'Several General operations selected',
        )}
        description={t(
          'catalog.easy_basket.total_strip.dialog_general_operations.description',
          'You have selected more than one GO for this operation. Do you wish to proceed?',
        )}
        status={'info'}
        content={
          <Flex minWidth={400} direction={'row'} justify={'space-evenly'}>
            <WhiteButton onClick={onClose}>
              <Flex minWidth={80} justify={'center'}>
                {t('common.action.cancel', 'Cancel')}
              </Flex>
            </WhiteButton>
            <BlackButton
              onClick={() => {
                onClose();
                selectLaborTime();
              }}
            >
              <Flex minWidth={80} justify={'center'}>
                {t('common.action.continue', 'Continue')}
              </Flex>
            </BlackButton>
          </Flex>
        }
        handleConfirm={() => undefined}
      />
    </CrosslinkCardWrapper>
  );
};

export default LaborTimeCard;
